/**
 * 타임존 목록 — 나라별로 묶어 고르게 한다.
 *
 * 예전에는 자유 입력이었다. `Asia/Seoul` 을 정확히 아는 사람만 쓸 수 있고, 오타는
 * 서버가 400 으로 막아 주지만 **`Asia/Tokyo` 를 잘못 고르는 것은 아무도 못 막는다** —
 * 한 시간씩 어긋난 편성이 조용히 나간다. 나라 이름으로 고르면 그 실수가 줄어든다.
 *
 * **목록은 두 겹이다.**
 *
 * 1. 손으로 고른 나라들. 한국어 나라·도시 이름을 붙여 실제로 배포하는 지역을 먼저 보여준다.
 * 2. 나머지 전부. `Intl.supportedValuesOf('timeZone')` 이 런타임에서 주는 완전한 목록이라
 *    여기 안 적힌 지역이 있어도 막히지 않고, 목록이 낡지도 않는다.
 *
 * 손으로 고른 목록만 두면 언젠가 반드시 빠진 나라가 나오고, 그때 사용자는 아무 방법도
 * 없다. 전체 목록만 두면 418개가 알파벳 순으로 늘어서 한국을 찾는 데 스크롤을 한참 한다.
 */

export interface TimezoneOption {
  /** IANA 식별자. 저장되는 값. */
  zone: string;
  /** 선택지에 보이는 이름. 손으로 고른 것은 도시 한국어 이름, 나머지는 식별자 그대로. */
  label: string;
  /** 지금 이 지역의 UTC 오프셋. 서머타임이 반영된 값이라 계절에 따라 달라진다. */
  offset: string;
}

export interface TimezoneGroup {
  /** 나라 이름, 또는 나머지 묶음의 대륙 이름. */
  title: string;
  options: TimezoneOption[];
}

/**
 * 손으로 고른 나라들. 한국을 맨 앞에 둔다 — 대부분의 스케줄이 여기다.
 *
 * 나라 안에 여러 지역이 있으면 도시를 나눠 적는다. "미국" 하나로는 고를 수 없다.
 */
const CURATED: ReadonlyArray<{ country: string; zones: ReadonlyArray<[zone: string, city: string]> }> = [
  { country: '대한민국', zones: [['Asia/Seoul', '서울']] },
  { country: '일본', zones: [['Asia/Tokyo', '도쿄']] },
  {
    country: '중국',
    zones: [
      ['Asia/Shanghai', '상하이 · 베이징'],
      ['Asia/Urumqi', '우루무치'],
    ],
  },
  { country: '대만', zones: [['Asia/Taipei', '타이베이']] },
  { country: '홍콩', zones: [['Asia/Hong_Kong', '홍콩']] },
  { country: '싱가포르', zones: [['Asia/Singapore', '싱가포르']] },
  { country: '베트남', zones: [['Asia/Ho_Chi_Minh', '호치민 · 하노이']] },
  { country: '태국', zones: [['Asia/Bangkok', '방콕']] },
  { country: '말레이시아', zones: [['Asia/Kuala_Lumpur', '쿠알라룸푸르']] },
  {
    country: '인도네시아',
    zones: [
      ['Asia/Jakarta', '자카르타'],
      ['Asia/Makassar', '마카사르'],
      ['Asia/Jayapura', '자야푸라'],
    ],
  },
  { country: '필리핀', zones: [['Asia/Manila', '마닐라']] },
  { country: '인도', zones: [['Asia/Kolkata', '콜카타 · 뉴델리']] },
  { country: '아랍에미리트', zones: [['Asia/Dubai', '두바이']] },
  { country: '사우디아라비아', zones: [['Asia/Riyadh', '리야드']] },
  {
    country: '오스트레일리아',
    zones: [
      ['Australia/Sydney', '시드니'],
      ['Australia/Brisbane', '브리즈번'],
      ['Australia/Adelaide', '애들레이드'],
      ['Australia/Perth', '퍼스'],
    ],
  },
  { country: '뉴질랜드', zones: [['Pacific/Auckland', '오클랜드']] },
  {
    country: '미국',
    zones: [
      ['America/New_York', '뉴욕 (동부)'],
      ['America/Chicago', '시카고 (중부)'],
      ['America/Denver', '덴버 (산악)'],
      ['America/Phoenix', '피닉스 (애리조나)'],
      ['America/Los_Angeles', '로스앤젤레스 (태평양)'],
      ['America/Anchorage', '앵커리지 (알래스카)'],
      ['Pacific/Honolulu', '호놀룰루 (하와이)'],
    ],
  },
  {
    country: '캐나다',
    zones: [
      ['America/Toronto', '토론토'],
      ['America/Winnipeg', '위니펙'],
      ['America/Edmonton', '에드먼턴'],
      ['America/Vancouver', '밴쿠버'],
    ],
  },
  { country: '멕시코', zones: [['America/Mexico_City', '멕시코시티']] },
  {
    country: '브라질',
    zones: [
      ['America/Sao_Paulo', '상파울루'],
      ['America/Manaus', '마나우스'],
    ],
  },
  { country: '영국', zones: [['Europe/London', '런던']] },
  { country: '아일랜드', zones: [['Europe/Dublin', '더블린']] },
  { country: '프랑스', zones: [['Europe/Paris', '파리']] },
  { country: '독일', zones: [['Europe/Berlin', '베를린']] },
  { country: '네덜란드', zones: [['Europe/Amsterdam', '암스테르담']] },
  { country: '스페인', zones: [['Europe/Madrid', '마드리드']] },
  { country: '이탈리아', zones: [['Europe/Rome', '로마']] },
  { country: '폴란드', zones: [['Europe/Warsaw', '바르샤바']] },
  { country: '스웨덴', zones: [['Europe/Stockholm', '스톡홀름']] },
  { country: '튀르키예', zones: [['Europe/Istanbul', '이스탄불']] },
  {
    country: '러시아',
    zones: [
      ['Europe/Moscow', '모스크바'],
      ['Asia/Novosibirsk', '노보시비르스크'],
      ['Asia/Vladivostok', '블라디보스토크'],
    ],
  },
  { country: '이집트', zones: [['Africa/Cairo', '카이로']] },
  { country: '남아프리카공화국', zones: [['Africa/Johannesburg', '요하네스버그']] },
  { country: '나이지리아', zones: [['Africa/Lagos', '라고스']] },
  { country: '협정 세계시', zones: [['UTC', 'UTC']] },
];

/** 나머지 묶음의 대륙 이름. `Intl` 이 주는 식별자의 앞부분으로 가른다. */
const REGION_TITLES: Readonly<Record<string, string>> = {
  Africa: '그 외 · 아프리카',
  America: '그 외 · 아메리카',
  Antarctica: '그 외 · 남극',
  Arctic: '그 외 · 북극',
  Asia: '그 외 · 아시아',
  Atlantic: '그 외 · 대서양',
  Australia: '그 외 · 오스트레일리아',
  Europe: '그 외 · 유럽',
  Indian: '그 외 · 인도양',
  Pacific: '그 외 · 태평양',
};

/*
 * **결과가 아니라 포매터를 캐시한다.**
 *
 * 비싼 것은 `Intl.DateTimeFormat` 을 만드는 쪽이고, `formatToParts` 는 싸다. 라벨을
 * 캐시하면 계산은 한 번으로 줄지만 `now` 가 무시된다 — 여름에 한 번 계산해 두면 겨울에도
 * 여름 오프셋을 내놓는다. 418개를 매번 다시 만드는 것도, 서머타임이 굳는 것도 피한다.
 * `null` 은 "유효하지 않은 식별자" 를 기억해 둔 것이다.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat | null>();

function offsetFormatterFor(zone: string): Intl.DateTimeFormat | null {
  const cached = formatterCache.get(zone);
  if (cached !== undefined) return cached;
  let formatter: Intl.DateTimeFormat | null = null;
  try {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' });
  } catch {
    formatter = null;
  }
  formatterCache.set(zone, formatter);
  return formatter;
}

/**
 * `UTC+09:00` 형태의 오프셋. 주어진 시각 기준이라 서머타임이 반영된다.
 *
 * 유효하지 않은 식별자는 빈 문자열 — 목록에 없는 값을 그대로 보여줘야 하는 경우가 있고
 * (API 로 넣은 값), 거기서 예외를 던지면 편집기 자체가 안 열린다.
 */
export function offsetLabel(zone: string, now: Date = new Date()): string {
  const formatter = offsetFormatterFor(zone);
  if (!formatter) return '';
  // `GMT+09:00` · UTC 는 `GMT` 만 나온다.
  const raw = formatter.formatToParts(now).find((part) => part.type === 'timeZoneName')?.value ?? '';
  return raw === 'GMT' ? 'UTC+00:00' : raw.replace('GMT', 'UTC');
}

/** 런타임이 아는 전체 타임존. `Intl.supportedValuesOf` 가 없는 환경에서는 손으로 고른 것만 쓴다. */
function allZones(): string[] {
  const supported = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    return supported.supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
}

/**
 * 드롭다운에 넣을 묶음들.
 *
 * `current` 가 어느 묶음에도 없으면 **맨 앞에 따로 넣는다.** API 로 넣은 값이나 새 IANA
 * 지역일 수 있는데, 목록에 없다고 빼 버리면 편집기를 여는 것만으로 타임존이 조용히
 * 바뀐다 — 저장 버튼을 누르지 않아도 값이 달라져 있는 상태가 된다.
 */
export function timezoneGroups(current: string, now: Date = new Date()): TimezoneGroup[] {
  const groups: TimezoneGroup[] = [];
  const seen = new Set<string>();

  for (const { country, zones } of CURATED) {
    const options = zones.map(([zone, city]) => {
      seen.add(zone);
      return { zone, label: city, offset: offsetLabel(zone, now) };
    });
    groups.push({ title: country, options });
  }

  const rest = new Map<string, TimezoneOption[]>();
  for (const zone of allZones()) {
    if (seen.has(zone)) continue;
    seen.add(zone);
    const region = zone.split('/')[0] ?? '';
    const title = REGION_TITLES[region] ?? '그 외 · 기타';
    const bucket = rest.get(title) ?? [];
    bucket.push({ zone, label: zone, offset: offsetLabel(zone, now) });
    rest.set(title, bucket);
  }

  for (const title of [...rest.keys()].sort()) {
    groups.push({ title, options: (rest.get(title) ?? []).sort((a, b) => a.zone.localeCompare(b.zone)) });
  }

  if (current && !seen.has(current)) {
    groups.unshift({
      title: '현재 설정',
      options: [{ zone: current, label: current, offset: offsetLabel(current, now) }],
    });
  }

  return groups;
}
